import React, { useMemo, useState } from 'react';
import { Breadcrumb } from './Breadcrumb';
import {
  McTopBar,
  McList,
  McListItem,
  McIcon,
  McButton,
  McAvatar,
  McTooltip
} from '@maersk-global/mds-react-wrapper/components-core';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { roleConfig } from '../../config/roles';
import { PopupButton } from '@maersk-global/insightshub-embed-react';

const menuItems = [
  { text: 'Queries', icon: 'magnifying-glass', path: '/queries' },
  { text: 'Create Query', icon: 'plus', path: '/queries/create' },
  { text: 'Schedules', icon: 'calendar', path: '/schedules', role: roleConfig.viewSchedules },
  { text: 'Reference Data', icon: 'table', path: '/ref-data', role: roleConfig.viewRefData },
  { text: 'AI Agents', icon: 'robot', path: '/agents', role: roleConfig.viewAgents },
  { text: 'Regions', icon: 'globe', path: '/regions', role: roleConfig.viewRegions },
  { text: 'Cache Management', icon: 'database', path: '/admin/cache', role: roleConfig.manageCache },
  { text: 'User Quotas', icon: 'lock', path: '/admin/user-quotas', role: roleConfig.manageUserQuota },
  { text: 'Server Registry', icon: 'rack', path: '/admin/server-registry', role: roleConfig.viewServerRegistry, adminOnly: true },
  { text: 'Help Center', icon: 'question-circle', path: '/faq', role: roleConfig.viewFaq },
  { text: 'API Reference', icon: 'file', path: '/developer-docs', role: roleConfig.viewDevDocs },
];

export default function MainLayout({ children }: { children: React.ReactNode }) {
  const [open, setOpen] = useState(false);
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, authEnabled, logout, hasRole } = useAuth();

  const visibleMenuItems = useMemo(
    () => menuItems.filter((item) => !item.role || hasRole(item.role)),
    [hasRole]
  );

  // Show sidebar on all pages except the Home page
  const showDrawer = location.pathname !== '/';
  const drawerWidth = showDrawer ? (open ? 240 : 64) : 0;

  return (
    <div
      className="mds"
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100vh',
        overflow: 'hidden',
        '--app-main-drawer-width': `${drawerWidth}px`,
      } as React.CSSProperties}
    >
      <McTopBar
        product="PortSight"
        productshort="CDH Enterprise"
        style={{ zIndex: 1101 }}
      >
        <div slot="actions" style={{ display: 'flex', alignItems: 'center', gap: '16px', width: '100%', justifyContent: 'flex-end' }}>
          <McTooltip>
            <div slot="trigger">
              <McButton
                appearance="secondary"
                variant="outlined"
                onClick={() => navigate('/')}
                style={{ minWidth: 44, padding: '0 10px' }}
                aria-label="Home"
              >
                Home
              </McButton>
            </div>
            <span slot="content">Home</span>
          </McTooltip>

          <PopupButton id="edc65779-62cf-4255-88b4-9984c19c3ea1" as="span">
            <McTooltip>
              <div slot="trigger">
                <McButton
                  appearance="secondary"
                  variant="outlined"
                  style={{ minWidth: 44, padding: '0 10px' }}
                  aria-label="Onboarding request"
                >
                 Onboard
                </McButton>
              </div>
              <span slot="content">Onboard</span>
            </McTooltip>
          </PopupButton>

          <PopupButton id="56dd6aa6-1a61-4135-8693-ecc316ca8f25" as="span">
            <McTooltip>
              <div slot="trigger">
                <McButton
                  appearance="secondary"
                  variant="outlined"
                  style={{ minWidth: 44, padding: '0 10px' }}
                  aria-label="Feedback"
                >
                  Feedback
                </McButton>
              </div>
              <span slot="content">Feedback</span>
            </McTooltip>
          </PopupButton>
          {authEnabled && user && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <McAvatar
                initials={user.name?.split(' ').map((n: string) => n[0]).join('').substring(0, 2) || user.username?.substring(0, 2).toUpperCase()}
                appearance="color-1"
                fit="x-large"
                onClick={() => setShowProfileMenu(!showProfileMenu)}
                style={{ cursor: 'pointer' } as any}
              />
              {showProfileMenu && (
                <div style={{
                  position: 'absolute',
                  top: '64px',
                  right: '16px',
                  backgroundColor: 'white',
                  padding: '16px',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                  borderRadius: '4px',
                  zIndex: 1200,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '8px'
                }}>
                  <McButton
                    appearance="primary"
                    variant="outlined"
                    fit="large"
                    onClick={logout}
                    style={{ color: 'black' }}
                  >
                    <McIcon icon="arrow-right" slot="prefix" />
                    Sign out
                  </McButton>
                </div>
              )}
            </div>
          )}
        </div>
      </McTopBar>

      {location.pathname !== '/' && (
        <div
          style={{
            padding: '6px 16px 4px',
            borderBottom: '1px solid var(--mds-color-neutral-weak-decoration, #e5e7eb)',
            background: '#fff',
            zIndex: 1001,
          }}
        >
          <Breadcrumb />
        </div>
      )}

      <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        {showDrawer && (
          <aside style={{
            width: `${drawerWidth}px`,
            flexShrink: 0,
            transition: 'width 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
            borderRight: '1px solid var(--mds-color-neutral-weak-decoration, #e0e0e0)',
            backgroundColor: 'white',
            display: 'flex',
            flexDirection: 'column',
            zIndex: 1000,
            overflow: 'hidden'
          }}>
            <div style={{ padding: '12px 8px', borderBottom: '1px solid var(--mds-color-neutral-weak-decoration, #f0f0f0)', display: 'flex', justifyContent: 'center' }}>
              <McTooltip>
                <div slot="trigger" style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
                  <McButton
                    appearance={open ? 'primary' : 'neutral'}
                    variant={open ? 'filled' : 'outlined'}
                    onClick={() => setOpen((prev) => !prev)}
                    style={{ width: open ? 'auto' : '100%', display: 'flex', justifyContent: 'center' }}
                  >
                    <McIcon icon="sliders-horizontal" />
                  </McButton>
                </div>
                <span slot="content">{open ? 'Collapse Sidebar' : 'Expand Sidebar'}</span>
              </McTooltip>
            </div>

            <nav style={{ flex: 1, padding: '8px', overflowY: 'auto', overflowX: 'hidden' }}>
              <McList>
                {visibleMenuItems.map((item) => {
                  const isActive = location.pathname.startsWith(item.path);

                  const listItem = (
                    <McListItem
                      label={open ? item.text : ''}
                      icon={item.icon}
                      hiddenlabel={!open}
                      onClick={() => navigate(item.path)}
                      style={{
                        backgroundColor: isActive ? 'var(--mds-color-primary-weak-decoration)' : 'transparent',
                        borderRadius: '4px',
                        marginBottom: '4px',
                        cursor: 'pointer',
                        width: '100%',
                        '--mc-list-item-icon-color': isActive ? 'var(--mds-color-primary-60)' : 'var(--mds-color-neutral-60)',
                        '--mc-list-item-label-color': isActive ? 'var(--mds-color-primary-60)' : 'inherit'
                      } as any}
                    />
                  );

                  return (
                    <div key={item.path} style={{ width: '100%' }}>
                      {!open ? (
                        <McTooltip position="right-center">
                          <div slot="trigger">
                            {listItem}
                          </div>
                          <span slot="content">{item.text}</span>
                        </McTooltip>
                      ) : (
                        listItem
                      )}
                    </div>
                  );
                })}
              </McList>
            </nav>
          </aside>
        )}

        <main style={{
          flex: 1,
          overflowY: 'auto',
          overflowX: 'hidden',
          padding: '16px',
          backgroundColor: 'var(--mds-color-neutral-canvas-background, #F5F5F5)'
        }}>
          {children}
        </main>
      </div>
    </div>
  );
}
